import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { Tooltip } from "react-tooltip";
import Link from "next/link";
import Image from "next/image";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import DropVoteProgressing from "@/components/drops/view/utils/DropVoteProgressing";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { WAVE_VOTING_LABELS, WAVE_VOTE_STATS_LABELS } from "@/helpers/waves/waves.constants";
import { getApprovalDropStatus } from "@/helpers/waves/approve-wave.helpers";
interface WaveLeaderboardDropRatersProps {
  readonly drop: ExtendedDrop;
  readonly winningThreshold?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
}
export const WaveLeaderboardDropRaters: React.FC<WaveLeaderboardDropRatersProps> = t0 => {
  const $ = _c(54);
  const {
    drop,
    winningThreshold,
    isVotingClosed: t1
  } = t0;
  const isVotingClosed = t1 === undefined ? false : t1;
  const votersCountLabel = drop.raters_count === 1 ? "voter" : "voters";
  const totalVote = drop.rating;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const votingLabel = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  const totalVoteClass = totalVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const userVoteClass = userVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const displayWinningThreshold = typeof winningThreshold === "number" && winningThreshold > 0 ? winningThreshold : null;
  const hasWinningThreshold = displayWinningThreshold !== null;
  let approvalStatusClass;
  let t2;
  if ($[0] !== displayWinningThreshold || $[1] !== drop || $[2] !== isVotingClosed) {
    const approvalStatus = displayWinningThreshold !== null ? getApprovalDropStatus({
      drop,
      isClosed: isVotingClosed,
      winningThreshold: displayWinningThreshold
    }) : null;
    if (approvalStatus?.kind === "approved" || approvalStatus?.kind === "reached_threshold") {
      approvalStatusClass = "tw-text-emerald-400";
    } else {
      if (approvalStatus?.kind === "closed") {
        approvalStatusClass = "tw-text-amber-300";
      }
    }
    bb0: {
      if (approvalStatus === null) {
        t2 = null;
        break bb0;
      }
      if (approvalStatus.kind === "approved") {
        t2 = "Approved";
        break bb0;
      }
      if (approvalStatus.kind === "reached_threshold") {
        t2 = "Reached threshold";
        break bb0;
      }
      if (approvalStatus.kind === "closed") {
        t2 = "Closed";
        break bb0;
      }
      t2 = `Needs ${formatNumberWithCommas(approvalStatus.remaining ?? 0)}`;
    }
    $[0] = displayWinningThreshold;
    $[1] = drop;
    $[2] = isVotingClosed;
    $[3] = approvalStatusClass;
    $[4] = t2;
  } else {
    approvalStatusClass = $[3];
    t2 = $[4];
  }
  const approvalStatusLabel = t2;
  const hasUserVoted = userVote !== 0;
  const t3 = `tw-font-medium ${totalVoteClass}`;
  let t4;
  if ($[5] !== totalVote) {
    t4 = formatNumberWithCommas(totalVote);
    $[5] = totalVote;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== displayWinningThreshold) {
    t6 = displayWinningThreshold !== null && <><span className="tw-font-medium tw-text-iron-500">/</span><span className="tw-font-medium tw-text-iron-50">{formatNumberWithCommas(displayWinningThreshold)}</span></>;
    $[10] = displayWinningThreshold;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== drop.rating || $[13] !== drop.rating_prediction) {
    t7 = <DropVoteProgressing current={drop.rating} projected={drop.rating_prediction} compact={true} />;
    $[12] = drop.rating;
    $[13] = drop.rating_prediction;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className="tw-relative tw-inline-flex tw-items-center tw-gap-x-1.5">{t5}{t6}{t7}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== approvalStatusClass || $[20] !== approvalStatusLabel || $[21] !== hasWinningThreshold || $[22] !== votingLabel) {
    t9 = <span className="tw-whitespace-nowrap tw-font-normal tw-text-iron-400">{hasWinningThreshold && approvalStatusLabel !== null ? <span className={approvalStatusClass}>{approvalStatusLabel}</span> : <>{votingLabel} {WAVE_VOTE_STATS_LABELS.TOTAL}</>}</span>;
    $[19] = approvalStatusClass;
    $[20] = approvalStatusLabel;
    $[21] = hasWinningThreshold;
    $[22] = votingLabel;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8 || $[25] !== t9) {
    t10 = <div className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-leading-5">{t8}{t9}</div>;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== drop.id || $[28] !== drop.top_raters || $[29] !== votingLabel) {
    let t12;
    if ($[31] !== drop.id || $[32] !== drop.top_raters.length || $[33] !== votingLabel) {
      t12 = (voter, index) => {
        const voterLabel = voter.profile.handle ?? voter.profile.primary_address;
        const tooltipId = `voter-${drop.id}-${voter.profile.id}`;
        return <React.Fragment key={voter.profile.id}><div className="tw-relative tw-transition-transform hover:tw-z-10 hover:tw-scale-110" style={{
            zIndex: drop.top_raters.length - index
          }} data-tooltip-id={tooltipId}><Link href={`/${voterLabel}`}>{voter.profile.pfp ? <Image src={getScaledImageUri(voter.profile.pfp, ImageScale.W_AUTO_H_50)} alt={`${voterLabel}'s Profile`} width={20} height={20} className="tw-h-5 tw-w-5 tw-rounded-md tw-bg-iron-800 tw-object-contain tw-ring-1 tw-ring-black" /> : <div className="tw-h-5 tw-w-5 tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-black" />}</Link></div><Tooltip id={tooltipId} place="top" offset={8} opacity={1} positionStrategy="fixed" delayShow={0} style={TOOLTIP_STYLES}>{voterLabel} • {formatNumberWithCommas(voter.rating)}{" "}{votingLabel}</Tooltip></React.Fragment>;
      };
      $[31] = drop.id;
      $[32] = drop.top_raters.length;
      $[33] = votingLabel;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    t11 = drop.top_raters.map(t12);
    $[27] = drop.id;
    $[28] = drop.top_raters;
    $[29] = votingLabel;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[35] !== t11) {
    t12 = <div className="tw-flex tw-items-center -tw-space-x-1.5">{t11}</div>;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== drop.raters_count) {
    t13 = formatNumberWithCommas(drop.raters_count);
    $[37] = drop.raters_count;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13 || $[40] !== votersCountLabel) {
    t14 = <span className="tw-text-iron-400">{t13} {votersCountLabel}</span>;
    $[39] = t13;
    $[40] = votersCountLabel;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t12 || $[43] !== t14) {
    t15 = <div className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap tw-text-sm tw-leading-5">{t12}{t14}</div>;
    $[42] = t12;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== hasUserVoted || $[46] !== userVote || $[47] !== userVoteClass || $[48] !== votingLabel) {
    t16 = hasUserVoted && <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-leading-5"><span className="tw-whitespace-nowrap"><span className="tw-font-normal tw-text-iron-400">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:{" "}</span><span className={`tw-font-medium ${userVoteClass}`}>{formatNumberWithCommas(userVote)} {votingLabel}</span></span></div>;
    $[45] = hasUserVoted;
    $[46] = userVote;
    $[47] = userVoteClass;
    $[48] = votingLabel;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t10 || $[51] !== t15 || $[52] !== t16) {
    t17 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 sm:tw-justify-end">{t10}{t15}{t16}</div>;
    $[50] = t10;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  return t17;
};